import { c as _c } from "react/compiler-runtime";
import { Tooltip } from '@systemeio/ui';
import React, { type JSX } from 'react';
import { InstanceWithNestedIdType } from 'shared/components/table/types/instance-interface';
interface TableDataInterface<T> {
  items: T[];
  [key: string]: any;
}
type AlignType = 'justify-end' | 'justify-start' | 'justify-center';
export interface TabColumnDefinitionInterface<T, K> {
  key: keyof T | T[keyof T];
  header: string | JSX.Element;
  onHeaderRender?: (data?: K) => JSX.Element;
  subHeaders: {
    widthPercent?: number;
    title: string | JSX.Element;
    onRender?: (instance: T) => JSX.Element;
    onRenderSmall?: (instance: T) => JSX.Element;
    align?: AlignType;
  }[];
  subHeaderWidthPercent?: number[];
  widthPercent?: number;
  shouldRender?: boolean;
}
export interface TableProps<T, K> {
  columns: TabColumnDefinitionInterface<T, K>[];
  data?: K;
  passDataToHeader?: boolean;
}
function TabTable(t0) {
  const $ = _c(20);
  const {
    columns,
    data,
    passDataToHeader
  } = t0;
  let t1;
  if ($[0] !== columns) {
    t1 = columns.filter(_temp).reduce(_temp2, 0);
    $[0] = columns;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const totalWidth = t1;
  const rightBorderStyles = data ? "" : "last:lg:border-r";
  const t2 = `flex w-full flex-col lg:border-t ${data ? "lg:border-r" : ""} lg:border-text-primary/15 text-text-primary/60 lg:text-text-primary/100 lg:text-sm`;
  const t3 = passDataToHeader ? data : undefined;
  let t4;
  if ($[2] !== columns || $[3] !== rightBorderStyles || $[4] !== t3 || $[5] !== totalWidth) {
    t4 = <TableHeader columns={columns} totalWidth={totalWidth} rightBorderStyles={rightBorderStyles} data={t3} />;
    $[2] = columns;
    $[3] = rightBorderStyles;
    $[4] = t3;
    $[5] = totalWidth;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== columns || $[8] !== rightBorderStyles || $[9] !== totalWidth) {
    t5 = <TableSubHeader columns={columns} totalWidth={totalWidth} rightBorderStyles={rightBorderStyles} />;
    $[7] = columns;
    $[8] = rightBorderStyles;
    $[9] = totalWidth;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== columns || $[12] !== data || $[13] !== totalWidth) {
    t6 = <div className="overflow-auto"><TableRows columns={columns} data={data} totalWidth={totalWidth} /></div>;
    $[11] = columns;
    $[12] = data;
    $[13] = totalWidth;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t2 || $[16] !== t4 || $[17] !== t5 || $[18] !== t6) {
    t7 = <div className={t2}>{t4}{t5}{t6}</div>;
    $[15] = t2;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function _temp2(prev, cur) {
  return prev + (cur.widthPercent || 25);
}
function _temp(c) {
  return c.widthPercent !== undefined;
}
function TableHeader(t0) {
  const $ = _c(11);
  const {
    columns,
    totalWidth,
    rightBorderStyles,
    data
  } = t0;
  let t1;
  if ($[0] !== columns || $[1] !== data || $[2] !== rightBorderStyles || $[3] !== totalWidth) {
    let t2;
    if ($[5] !== data || $[6] !== rightBorderStyles || $[7] !== totalWidth) {
      t2 = (column, index) => {
        const widthPercent = column.widthPercent || 25;
        const onRender = column.onHeaderRender && data ? column.onHeaderRender(data) : column.header;
        return <div key={`header-cell-${index}`} className={`lg:border-text-primary/15 text-text-primary font-inter hidden truncate text-sm font-bold lg:table-cell lg:border-b lg:border-l lg:text-[15px] ${rightBorderStyles}`} style={{
          flex: `${widthPercent / totalWidth * 100}`
        }}>{typeof onRender === "string" ? <Tooltip mode="overflow" label={column.header} wrapperClassName="justify-center" className="flex h-14 w-full items-center justify-center px-3"><span className="inline-block w-full truncate">{onRender}</span></Tooltip> : <div className="flex h-14 w-full min-w-0 items-center justify-center truncate"><div className="truncate">{onRender}</div></div>}</div>;
      };
      $[5] = data;
      $[6] = rightBorderStyles;
      $[7] = totalWidth;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t1 = columns.map(t2);
    $[0] = columns;
    $[1] = data;
    $[2] = rightBorderStyles;
    $[3] = totalWidth;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const headers = t1;
  let t2;
  if ($[9] !== headers) {
    t2 = <div className="bg-background-primary/40 flex items-center">{headers}</div>;
    $[9] = headers;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
function TableSubHeader(t0) {
  const $ = _c(9);
  const {
    columns,
    totalWidth,
    rightBorderStyles
  } = t0;
  let t1;
  if ($[0] !== columns || $[1] !== rightBorderStyles || $[2] !== totalWidth) {
    let t2;
    if ($[4] !== rightBorderStyles || $[5] !== totalWidth) {
      t2 = (column, index) => {
        const columnWidthPercent = column.widthPercent || 25;
        return <div key={`sub-header-header-cell-${index}`} className={`lg:border-text-primary/15 text-text-primary font-inter hidden items-center truncate text-sm font-bold lg:flex lg:border-l lg:text-[15px]  ${rightBorderStyles}`} style={{
          flex: `${columnWidthPercent / totalWidth * 100}`
        }}>{column.subHeaders.map((subHeader, index_0) => {
            const widthPercent = subHeader.widthPercent || 50;
            return <div key={`sub-header-cell-${index_0}`} className="lg:border-text-primary/15 hidden truncate px-3 first:border-none lg:table-cell lg:border-l" style={{
              flex: `${widthPercent / totalWidth * 100}`
            }}><Tooltip mode="overflow" label={subHeader.title} className="flex h-14 w-full items-center justify-center" wrapperClassName="justify-center"><span className="truncate font-normal">{subHeader.title}</span></Tooltip></div>;
          })}</div>;
      };
      $[4] = rightBorderStyles;
      $[5] = totalWidth;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = columns.map(t2);
    $[0] = columns;
    $[1] = rightBorderStyles;
    $[2] = totalWidth;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const subHeaders = t1;
  let t2;
  if ($[7] !== subHeaders) {
    t2 = <div className="bg-background-primary/40 flex items-center">{subHeaders}</div>;
    $[7] = subHeaders;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function TableRows(t0) {
  const $ = _c(9);
  const {
    columns,
    data,
    totalWidth
  } = t0;
  if (data === undefined) {
    let t1;
    if ($[0] !== columns || $[1] !== totalWidth) {
      t1 = <TableSkeleton columns={columns} totalWidth={totalWidth} />;
      $[0] = columns;
      $[1] = totalWidth;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== columns || $[4] !== data || $[5] !== totalWidth) {
    t1 = data.items.map((row, index) => <div className="mb-4 flex shrink-0 flex-col gap-2 lg:mb-0 lg:flex-row lg:gap-0" key={`row-${index}`}>{columns.map((column, columnIndex) => {
        const columnHeaderRender = column.onHeaderRender && data ? column.onHeaderRender(data) : column.header;
        const columnWidthPercent = column.widthPercent || 25;
        return <React.Fragment key={`column-parent-header-${columnIndex}`}>{column.subHeaders.length > 0 && <div className="text-text-primary font-medium lg:hidden">{columnHeaderRender}</div>}<div key={`sub-header-header-cell-${index}`} className="lg:border-text-primary/15 flex flex-col truncate lg:flex-row lg:border-b lg:border-l" style={{
            flex: `${columnWidthPercent / totalWidth * 100}`
          }}>{column.subHeaders.map((subHeader, subHeaderIndex) => {
              const widthPercent = subHeader.widthPercent || 100;
              const onRender = subHeader.onRender ? subHeader.onRender(row) : subHeader.title;
              const onRenderSmall = subHeader.onRenderSmall ? subHeader.onRenderSmall(row) : subHeader.onRender ? subHeader.onRender(row) : subHeader.title;
              const subheaderTitle = subHeader.title ? subHeader.title : column.header;
              const subheaderCalculatedTitle = typeof subheaderTitle === "string" && !!subheaderTitle.trim() ? `${subheaderTitle} :` : "";
              const node = <><div className={`flex items-center lg:hidden ${!!subheaderCalculatedTitle ? "gap-1" : ""}`}>{subHeader.onRenderSmall ? onRenderSmall : <><span className="text-text-primary/60 whitespace-nowrap lg:font-bold">{subheaderCalculatedTitle}</span>{onRender}</>}</div><div className={`hidden truncate lg:flex ${subHeader.align ? subHeader.align : "justify-center"} `}>{onRender}</div></>;
              return <div key={`sub-header-cell-${subHeaderIndex}`} className="lg:border-text-primary/15 flex flex-col truncate first:border-none lg:table-cell lg:border-l lg:px-3 lg:py-3.5" style={{
                flex: `${widthPercent / totalWidth * 100}`
              }}>{node}</div>;
            })}</div></React.Fragment>;
      })}</div>);
    $[3] = columns;
    $[4] = data;
    $[5] = totalWidth;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <>{t1}</>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function TableSkeleton(t0) {
  const $ = _c(4);
  const {
    columns,
    totalWidth
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Array.from(Array(6).keys());
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== columns || $[2] !== totalWidth) {
    t2 = <>{t1.map((row, index) => <div key={`row-${index}`} className={`even:bg-brand-primary-subtle odd:bg-background-secondary group flex animate-pulse flex-col items-start gap-4 border border-transparent px-6 py-4 lg:flex-row lg:items-center lg:gap-0 lg:p-0 ${index === 5 && "rounded-b-lg"}`}>{columns.map((column, index2) => {
          if (!column.widthPercent) {
            column.widthPercent = 25;
          }
          const widthPercent = column.widthPercent || 25;
          return <div className="w-full lg:py-4 lg:pl-2 lg:pr-4" key={`cell-${index2}`} style={{
            flex: `${widthPercent / totalWidth * 100}`
          }}><div className="bg-skeleton-primary rounded-sm text-transparent">.</div></div>;
        })}</div>)}</>;
    $[1] = columns;
    $[2] = totalWidth;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export default TabTable;